import { useState } from 'react';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { auth } from '../config/firebase';
import MaintenanceCard from '../components/MaintenanceCard';

function Settings() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setStatus(null);

    if (newPassword !== confirmPassword) {
      setStatus({ type: 'error', text: 'Las contraseñas nuevas no coinciden.' });
      return;
    }

    if (newPassword.length < 6) {
      setStatus({ type: 'error', text: 'La nueva contraseña debe tener al menos 6 caracteres.' });
      return;
    }

    setLoading(true);
    try {
      const user = auth.currentUser;
      if (!user || !user.email) {
        throw new Error('No se pudo identificar al usuario actual.');
      }

      // Re-authenticate first
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, newPassword);

      setStatus({ type: 'success', text: 'Contraseña actualizada correctamente.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error(error);
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setStatus({ type: 'error', text: 'La contraseña actual es incorrecta.' });
      } else if (error.code === 'auth/too-many-requests') {
        setStatus({ type: 'error', text: 'Demasiados intentos. Por favor, inténtalo de nuevo más tarde.' });
      } else {
        setStatus({ type: 'error', text: 'Ocurrió un error al actualizar la contraseña.' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-fade-in" style={{ paddingBottom: '2rem' }}>
      <div className="page-header" style={{ marginBottom: '2rem' }}>
        <div>
          <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '2rem' }}>⚙️</span> Configuración
          </h2>
          <p className="page-subtitle">Gestiona tu cuenta de administrador y preferencias de seguridad</p>
        </div>
      </div>

      <MaintenanceCard />

      <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', flexWrap: 'wrap' }}>
        <div className="card" style={{ 
          width: '100%',
          maxWidth: '600px',
          padding: '3rem', 
          position: 'relative',
          background: 'rgba(20, 20, 20, 0.6)',
          backdropFilter: 'blur(24px)',
          border: '1px solid rgba(255, 215, 0, 0.15)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)',
          borderRadius: '32px',
          overflow: 'hidden'
        }}>
          {/* Subtle background glow */}
          <div style={{ position: 'absolute', top: '0', right: '0', width: '100%', height: '100%', background: 'radial-gradient(circle at top right, rgba(255, 215, 0, 0.08) 0%, transparent 60%)', pointerEvents: 'none', borderRadius: '32px' }} />
          
          <div style={{ position: 'relative', zIndex: 1 }}>
            <h3 style={{ fontSize: '1.5rem', color: '#FFD700', marginBottom: '0.5rem', fontWeight: 'bold' }}>Seguridad</h3>
            <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '2rem', fontSize: '0.95rem' }}>
              Modifica tu contraseña de acceso al panel de administración. Por seguridad, te pediremos tu contraseña actual.
            </p>

            <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div>
                <label className="form-label" style={{ color: '#fff', fontSize: '0.95rem', marginBottom: '8px' }}>
                  Contraseña Actual
                </label>
                <input
                  type="password"
                  className="form-control"
                  placeholder="Escribe tu contraseña actual"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  style={{ 
                    width: '100%',
                    boxSizing: 'border-box',
                    background: 'rgba(0,0,0,0.4)', 
                    border: '1px solid rgba(255,255,255,0.1)', 
                    height: '52px', 
                    fontSize: '1.05rem',
                    borderRadius: '12px',
                    padding: '0 16px',
                    color: '#fff'
                  }}
                />
              </div>

              <div>
                <label className="form-label" style={{ color: '#fff', fontSize: '0.95rem', marginBottom: '8px' }}>
                  Nueva Contraseña
                </label>
                <input
                  type="password"
                  className="form-control"
                  placeholder="Mínimo 6 caracteres"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  style={{ 
                    width: '100%',
                    boxSizing: 'border-box',
                    background: 'rgba(0,0,0,0.4)', 
                    border: '1px solid rgba(255,255,255,0.1)', 
                    height: '52px', 
                    fontSize: '1.05rem',
                    borderRadius: '12px',
                    padding: '0 16px',
                    color: '#fff'
                  }}
                />
              </div>

              <div>
                <label className="form-label" style={{ color: '#fff', fontSize: '0.95rem', marginBottom: '8px' }}>
                  Confirmar Nueva Contraseña
                </label>
                <input
                  type="password"
                  className="form-control"
                  placeholder="Repite la nueva contraseña"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  style={{ 
                    width: '100%',
                    boxSizing: 'border-box',
                    background: 'rgba(0,0,0,0.4)', 
                    border: '1px solid rgba(255,255,255,0.1)', 
                    height: '52px', 
                    fontSize: '1.05rem',
                    borderRadius: '12px',
                    padding: '0 16px',
                    color: '#fff'
                  }}
                />
              </div>

              {status && (
                <div className="animate-fade-in" style={{
                  background: status.type === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                  border: `1px solid ${status.type === 'success' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                  padding: '1rem',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  color: status.type === 'success' ? '#10b981' : '#ef4444',
                  fontSize: '1rem',
                  fontWeight: 600,
                  marginTop: '0.5rem'
                }}>
                  <span style={{ fontSize: '1.25rem' }}>{status.type === 'success' ? '✅' : '⚠️'}</span>
                  {status.text}
                </div>
              )}

              <button 
                type="submit" 
                className="btn"
                disabled={loading || !currentPassword || !newPassword || !confirmPassword}
                style={{ 
                  marginTop: '1.5rem', 
                  height: '56px', 
                  fontSize: '1.1rem', 
                  fontWeight: 'bold',
                  background: (loading || !currentPassword || !newPassword || !confirmPassword) ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #FFD700 0%, #F59E0B 100%)',
                  color: (loading || !currentPassword || !newPassword || !confirmPassword) ? 'rgba(255,255,255,0.3)' : '#000',
                  border: 'none',
                  borderRadius: '16px',
                  boxShadow: (loading || !currentPassword || !newPassword || !confirmPassword) ? 'none' : '0 8px 20px rgba(255, 215, 0, 0.3)',
                  cursor: (loading || !currentPassword || !newPassword || !confirmPassword) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                {loading ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'center' }}>
                    <span className="spinner" style={{ width: '20px', height: '20px', borderTopColor: '#000' }} />
                    Actualizando...
                  </span>
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                    🔒 Actualizar Contraseña
                  </span>
                )}
              </button>
            </form>
          </div>
        </div>

      </div>
    </div>
  );
}

export default Settings;
